import React, { forwardRef, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Icon, D } from "./icons";
import { countWord, nf } from "./liveTrucks";
import RespondPanel from "../loads/RespondPanel";
import { LoadDetailsPanel } from "../ui/cards";
import RowMenu, { useRowMenu } from "../ui/RowMenu";
import { isCarrierRole } from "../useUnread";
import { fetchMyRequests } from "../../utils/matchingApi";
import { ORDER_TYPES, bodyTypeName, cityOf, fmtDateShort, money } from "../loads/common";

/**
 * «Найти грузы» results on «Главная» (iTruck_Home_Responsive searchLoads()): the same page, under the search window.
 * Columns: Грузоотправитель · Груз · Откуда · Куда · Погрузка · Ставка · Действия; sort «По соответствию» / «По ставке».
 * «Откликнуться» opens the offer panel (POST /m/loads/:id/propose), `···` → details / hide, like on «Поиск».
 * `rows` are already filtered by the page (form, verified, hidden rows).
 */
const HomeLoadResults = forwardRef(function HomeLoadResults({ rows, caption, byOwner, hid, action, onReset, say }, ref) {
  const user = useSelector((s) => s.auth?.user);
  const carrier = isCarrierRole(user?.role);
  const [sort, setSort] = useState("relevance");
  const [dialog, setDialog] = useState(null);
  const [matches, setMatches] = useState([]);
  const rowMenu = useRowMenu();

  const loadMatches = () =>
    carrier
      ? fetchMyRequests()
          .then((r) => setMatches(r?.matches || []))
          .catch(() => setMatches([]))
      : Promise.resolve();
  useEffect(() => {
    loadMatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carrier]);
  // a click in the map popup («Откликнуться» / «Детали груза»)
  useEffect(() => {
    if (action) setDialog({ type: action.type, order: action.order });
  }, [action]);
  const matchFor = (o) => matches.filter((m) => m.load?.id === o.uuid);
  const responded = (o) => matchFor(o).some((m) => m.decided);

  const sorted = useMemo(() => (sort === "rate" ? [...rows].sort((a, b) => (b.price || 0) - (a.price || 0)) : rows), [rows, sort]);

  return (
    <section className="results load-results" ref={ref}>
      <div className="results-heading">
        <div>
          <h2>
            Подходящие грузы <span id="result-count">{rows.length}</span>
          </h2>
          <p>{caption}</p>
          {hid.count ? (
            <button className="hidden-rows" type="button" onClick={hid.showAll}>
              Показать скрытые ({hid.count})
            </button>
          ) : null}
        </div>
        <label className="sort">
          <span>Сортировка:</span>
          <select id="sort" aria-label="Сортировка" value={sort} onChange={(e) => setSort(e.target.value)}>
            <option value="relevance">По соответствию</option>
            <option value="rate">По ставке</option>
          </select>
          <Icon d={D.chevron} />
        </label>
      </div>
      <div className="table-container">
        <table>
          <colgroup>
            <col style={{ width: "20%" }} />
            <col style={{ width: "19%" }} />
            <col style={{ width: "11%" }} />
            <col style={{ width: "11%" }} />
            <col style={{ width: "11%" }} />
            <col style={{ width: "11%" }} />
            <col style={{ width: "17%" }} />
          </colgroup>
          <thead>
            <tr>
              <th>Грузоотправитель</th>
              <th>Груз</th>
              <th>Откуда</th>
              <th>Куда</th>
              <th>Погрузка</th>
              <th>Ставка</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((o) => {
              const prof = byOwner[String(o.owner)];
              // the shipper's company name only — no logo, initials, contact person or phone in this column
              const company = prof?.company_name || o.contacts?.company_name || "—";
              const w = o.item?.weight;
              const v = o.item?.volume;
              const done = responded(o);
              return (
                <tr key={o.uuid}>
                  <td data-label="Грузоотправитель">
                    <div className="carrier">
                      <div>
                        <strong>
                          <span className="name" title={company}>
                            {company}
                          </span>
                          {prof?.verified ? (
                            <span className="verified" role="img" aria-label="Проверен" title="Проверенная компания">
                              ✓
                            </span>
                          ) : null}
                        </strong>
                      </div>
                    </div>
                  </td>
                  <td data-label="Груз">
                    <strong>{ORDER_TYPES[o.item?.type] || "Груз"}</strong>
                    <small>
                      {[bodyTypeName(o.transport_body_type), w != null ? `${nf(w)} т` : null, v != null ? `${nf(v)} м³` : null].filter(Boolean).join(" · ") || "—"}
                    </small>
                  </td>
                  <td data-label="Откуда">
                    <strong>{cityOf(o.from_address)}</strong>
                  </td>
                  <td data-label="Куда">
                    <strong>{cityOf(o.to_address)}</strong>
                  </td>
                  <td data-label="Погрузка">
                    <span>{fmtDateShort(o.from_date)}</span>
                    {o.to_date && o.to_date !== o.from_date ? <small>до {fmtDateShort(o.to_date)}</small> : null}
                  </td>
                  <td data-label="Ставка">
                    <b>{o.price ? money(o.price, o.currency) : "Торг"}</b>
                  </td>
                  <td data-label="Действия">
                    <div className="row-actions">
                      <button className="button primary request" type="button" disabled={done} onClick={() => setDialog({ type: "respond", order: o })}>
                        {done ? "Отклик отправлен" : "Откликнуться"}
                      </button>
                      <button className="button more" type="button" aria-haspopup="menu" aria-label="Действия с грузом" title="Действия с грузом" onClick={(e) => rowMenu.openMenu(e, o)}>
                        ···
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {rows.length === 0 && (
          <div className="empty">
            <h3>Подходящих грузов не найдено</h3>
            <p>Измените маршрут или параметры поиска.</p>
            <button className="button" type="button" onClick={onReset}>
              Сбросить фильтры
            </button>
          </div>
        )}
      </div>

      {rowMenu.menu && (
        <RowMenu
          anchor={rowMenu.menu.anchor}
          items={[
            { label: "Открыть детали", onClick: () => setDialog({ type: "more", order: rowMenu.menu.payload }) },
            { label: "Скрыть из результатов", onClick: () => hid.hide(rowMenu.menu.payload.uuid) },
          ]}
          onClose={rowMenu.closeMenu}
        />
      )}
      {dialog?.type === "more" && (
        <LoadDetailsPanel
          order={dialog.order}
          onClose={() => setDialog(null)}
          footer={
            <>
              <button className="button primary" type="button" disabled={responded(dialog.order)} onClick={() => setDialog({ type: "respond", order: dialog.order })}>
                {responded(dialog.order) ? "Предложение отправлено" : "Предложить транспорт"}
              </button>
              <button className="button" type="button" onClick={() => setDialog(null)}>
                Закрыть
              </button>
            </>
          }
        />
      )}
      {dialog?.type === "respond" && (
        <RespondPanel
          order={dialog.order}
          user={user}
          carrier={carrier}
          matchedVehicleIds={matchFor(dialog.order).filter((m) => !m.decided).map((m) => Number(m.vehicle_id))}
          onClose={() => setDialog(null)}
          onDone={(n, failed) => {
            say(failed ? `Отклик отправлен: ${n}, не удалось: ${failed}` : n > 1 ? `Отклик отправлен (${n} ${countWord(n, ["машина", "машины", "машин"])})` : "Отклик отправлен");
            loadMatches();
          }}
        />
      )}
    </section>
  );
});

export default HomeLoadResults;
